<template>
  <div class="identity-backdrop">
    <div class="identity-card">
      <div class="identity-card__brand">
        <div class="auto-auction-logo" aria-label="AutoAuction.com">
          <span class="auto-auction-logo__auto">Auto</span><span class="auto-auction-logo__auction">Auction</span><span class="auto-auction-logo__top-level-domain">.com</span>
        </div>
      </div>

      <div class="identity-card__actions">
        <template v-if="restorable">
          <h1 class="identity-card__title">{{ t('authentication.linking.restorableTitle') }}</h1>
          <p class="identity-card__lede">{{ t('authentication.linking.restorableLede') }}</p>
          <button class="button button--primary landing__primary" type="button" :disabled="busy" @click="restore">
            {{ t('authentication.linking.restoreButton') }}
          </button>
        </template>
        <template v-else-if="matchFound">
          <div class="match__head">
            <h1 class="identity-card__title">{{ t('authentication.linking.matchTitle') }}</h1>
            <span v-for="method in matchProviders" :key="method" class="provider-chip">
              <i class="pi" :class="providerIcon(method)" aria-hidden="true" />{{ providerLabel(method) }}
            </span>
          </div>
          <p v-if="signedInAs" class="identity-card__identity">
            {{ t('authentication.linking.notLinkedSignedInAs', { provider: signedInAs.provider }) }}
            <strong class="identity-card__identity-email">{{ displayEmail }}</strong>.
          </p>
          <p class="identity-card__lede">{{ t('authentication.linking.matchLede', { provider: signedInAs?.provider ?? '' }) }}</p>
          <button class="button button--primary landing__primary" type="button" :disabled="busy" @click="connectMatch">
            {{ t('authentication.linking.connectContinueButton') }}
          </button>
        </template>
        <template v-else>
          <h1 class="identity-card__title">{{ t('authentication.linking.notLinkedTitle') }}</h1>
          <p v-if="signedInAs" class="identity-card__identity">
            {{ t('authentication.linking.notLinkedSignedInAs', { provider: signedInAs.provider }) }}
            <strong class="identity-card__identity-email">{{ displayEmail }}</strong>.
          </p>
          <p class="identity-card__lede">{{ t('authentication.linking.notLinkedLede') }}</p>
          <button class="button button--primary landing__primary" type="button" :disabled="busy" @click="createAccount">
            {{ t('authentication.linking.createAccountButton') }}
          </button>
          <button class="button button--ghost landing__secondary" type="button" :disabled="busy" @click="explainerOpen = true">
            {{ t('authentication.linking.connectExistingButton') }}
          </button>
        </template>
        <p v-if="errorMessage" class="identity-card__error">{{ errorMessage }}</p>
        <button class="identity-card__walkaway" type="button" @click="signOut">
          {{ t('authentication.linking.notNowLink') }}
        </button>
      </div>

      <!-- The connect explainer: what "Continue" will do, in three steps, before
           the person is bounced through a second sign-in. It rises inside the
           card — the drawer anchors to the card and dims only the card. -->
      <AnchoredDrawer v-model:open="explainerOpen" :title="t('authentication.linking.sheetTitle')">
        <p class="connect-explainer__lede">{{ t('authentication.linking.sheetLede', { provider: signedInAs?.provider ?? '' }) }}</p>
        <ol class="connect-explainer__steps">
          <li><span class="connect-explainer__step-number">1</span>{{ t('authentication.linking.sheetStep1') }}</li>
          <li><span class="connect-explainer__step-number">2</span>{{ t('authentication.linking.sheetStep2') }}</li>
          <li><span class="connect-explainer__step-number">3</span>{{ t('authentication.linking.sheetStep3') }}</li>
        </ol>
        <template #footer>
          <div class="connect-explainer__actions">
            <button class="button button--primary connect-explainer__continue" type="button" :disabled="busy" @click="connectExisting">
              {{ t('authentication.linking.sheetContinueButton') }}
            </button>
            <button class="button button--ghost connect-explainer__cancel" type="button" @click="explainerOpen = false">
              {{ t('authentication.linking.sheetCancelButton') }}
            </button>
          </div>
        </template>
      </AnchoredDrawer>
    </div>
  </div>
</template>

<script setup>
import '../shared/identity-card.css';
import { computed, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { AnchoredDrawer } from '@hds/auto-auction-com-components-desktop';

import {
  IDENTITY_PATHS,
  brokeredLogin,
  connectToExistingAccount,
  createNewAccount,
  connectMatchedAccount,
  restoreClosedAccount,
  pendingLogin,
  sessionStore,
  sessionTokenStorage
} from '@hds/identity-client-sdk';

defineOptions({ name: 'AccountNotLinkedPage' });

// The ACCOUNT_NOT_LINKED choice screen (account-linking design, UI ruling 1)
// and, with ?mode=restorable, the closed-account restore offer; with
// ?mode=match, the verified-email match. The person proved a login that
// resolves to no live Human; the identity server holds it and this tab holds the
// token it issued for them, so the choices below can present it. This page never
// reveals whether a matching account exists
// (invariant 5) — only their own identity. Same brain as the mobile screen, in
// the desktop identity card; the connect explainer is an AnchoredDrawer over
// the card rather than a bottom sheet over the phone.

const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const session = sessionStore();

const explainerOpen = ref(false);
const busy = ref(false);
const errorMessage = ref('');
const signedInAs = ref(null);

const restorable = computed(() => route.query.mode === 'restorable');

// ?mode=match — the verified-email match variant ("you already have an
// account"). `providers` carries the existing login methods for the chips.
const matchFound = computed(() => route.query.mode === 'match');
const matchProviders = computed(() =>
  String(route.query.providers ?? '').split(',').filter(Boolean)
);

const PROVIDER_ICONS = { local: 'pi-envelope', google: 'pi-google', microsoft: 'pi-microsoft' };
function providerIcon(method) {
  return PROVIDER_ICONS[method] || 'pi-user';
}
function providerLabel(method) {
  if (method === 'local') return t('authentication.linking.providerLocal');
  if (method === 'google') return 'Google';
  if (method === 'microsoft') return 'Microsoft';
  return method;
}

// A long email never wraps mid-word; it middle-truncates so both the mailbox
// and the domain stay readable.
const displayEmail = computed(() => {
  // Only drawn under `v-if="signedInAs"`, and onMounted gives it an email string.
  const email = signedInAs.value.email;
  if (email.length <= 34) return email;
  return `${email.slice(0, 16)}…${email.slice(-14)}`;
});

// Display only: their OWN provider + email, as the identity server named the
// login they arrived with. No identity just means no identity line.
onMounted(() => {
  const identity = pendingLogin.getPendingLoginIdentity();
  if (!identity) return;
  signedInAs.value = { provider: providerLabel(identity.provider), email: identity.email || '' };
});

async function connectExisting() {
  busy.value = true;
  // Terminal: parks the current login as the one to attach and
  // navigates to sign-in for the account they already use.
  await connectToExistingAccount();
}

async function createAccount() {
  busy.value = true;
  errorMessage.value = '';
  try {
    await createNewAccount();
    await session.hydrate();
    router.replace(IDENTITY_PATHS.welcome);
  } catch (e) {
    errorMessage.value = e?.body?.error === 'CONNECTION_IN_USE'
      ? t('authentication.linking.connectionInUse')
      : t('authentication.linking.errorGeneric');
    busy.value = false;
  }
}

async function connectMatch() {
  busy.value = true;
  errorMessage.value = '';
  try {
    // One-tap attach on the verified-email match, then hydrate signs them into
    // the now-linked existing account.
    await connectMatchedAccount();
    await session.hydrate();
    router.replace('/');
  } catch (e) {
    errorMessage.value = e?.body?.error === 'CONNECTION_IN_USE'
      ? t('authentication.linking.connectionInUse')
      : t('authentication.linking.errorGeneric');
    busy.value = false;
  }
}

async function restore() {
  busy.value = true;
  errorMessage.value = '';
  try {
    await restoreClosedAccount();
    await session.hydrate();
    router.replace('/');
  } catch {
    errorMessage.value = t('authentication.linking.errorGeneric');
    busy.value = false;
  }
}

async function signOut() {
  await sessionTokenStorage.clearSession();
  pendingLogin.clearPendingLogin();
  // Terminal: kill the provider's session too, or the on-load login
  // silently returns the same unlinked identity.
  brokeredLogin.beginLogout();
}
</script>

<style scoped>
/* The shared card skin (backdrop, card, brand, actions, buttons) lives in
   ../shared/identity-card.css — only this page's chips and explainer are
   styled here. */

/* --- The match variant: title + a chip per existing login method -------- */

.match__head {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 12px;
}

.match__head .identity-card__title {
  margin: 0;
}

.provider-chip {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  color: var(--p-text-color);
  background: color-mix(in srgb, var(--p-text-color) 6%, transparent);
  border: 1px solid var(--p-content-border-color);
  border-radius: 999px;
  padding: 7px 13px;
}

.provider-chip .pi {
  font-size: 13px;
  color: var(--p-primary-color);
}

html:not(.p-dark) .provider-chip .pi {
  color: var(--p-primary-700);
}

/* --- The connect explainer, inside the card's drawer -------------------- */

.connect-explainer__lede {
  font-size: 14px;
  line-height: 1.55;
  color: var(--p-text-hover-muted-color);
  margin: 0 0 14px;
}

.connect-explainer__steps {
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 10px;
  font-size: 14px;
  color: var(--p-text-hover-muted-color);
}

.connect-explainer__steps li {
  display: flex;
  gap: 10px;
  align-items: baseline;
}

.connect-explainer__step-number {
  font-family: var(--p-font-mono);
  font-size: 12px;
  color: var(--p-primary-color);
}

/* Light mode: the mint accent is too pale on the light card, so the step
   numbers drop to a darker mint (primary-700). */
html:not(.p-dark) .connect-explainer__step-number {
  color: var(--p-primary-700);
}

.connect-explainer__actions {
  display: flex;
  flex-direction: column;
}

.connect-explainer__actions .button--ghost {
  margin-bottom: 0;
}
</style>
